import React, { useState } from 'react';
import { Text, makeStyles, tokens } from '@fluentui/react-components';
import { ChevronDown12Regular, ChevronRight12Regular } from '@fluentui/react-icons';
import SemanticViewCard from './SemanticViewCard';
import type { DataModelView } from '../../api/dataModelsService';

const useStyles = makeStyles({
  panel: {
    width: '340px',
    minWidth: '340px',
    height: '100%',
    overflowY: 'auto',
    padding: '12px',
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  group: {
    marginBottom: '12px',
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 0',
    cursor: 'pointer',
    userSelect: 'none',
    ':hover': { opacity: 0.8 },
  },
  cards: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginTop: '6px',
  },
  empty: {
    padding: '32px 16px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

interface SemanticViewsPanelProps {
  views: DataModelView[];
  selectedViewId: string | null;
  onViewSelect: (viewId: string) => void;
}

interface GroupProps {
  title: string;
  count: number;
  views: DataModelView[];
  selectedViewId: string | null;
  onViewSelect: (viewId: string) => void;
}

const ViewGroup: React.FC<GroupProps> = ({ title, count, views, selectedViewId, onViewSelect }) => {
  const styles = useStyles();
  const [expanded, setExpanded] = useState(true);

  return (
    <div className={styles.group}>
      <div className={styles.groupHeader} onClick={() => setExpanded((v) => !v)}>
        {expanded ? <ChevronDown12Regular /> : <ChevronRight12Regular />}
        <Text weight="semibold" size={200}>{title}</Text>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>({count})</Text>
      </div>
      {expanded && (
        <div className={styles.cards}>
          {views.map((v) => (
            <SemanticViewCard key={v.id} view={v} isSelected={selectedViewId === v.id} onSelect={onViewSelect} />
          ))}
        </div>
      )}
    </div>
  );
};

const SemanticViewsPanel: React.FC<SemanticViewsPanelProps> = ({
  views,
  selectedViewId,
  onViewSelect,
}) => {
  const styles = useStyles();

  const factViews = views.filter((v) => (v.viewType ?? 'fact') === 'fact').sort((a, b) => a.sortOrder - b.sortOrder);
  const multiFactViews = views.filter((v) => v.viewType === 'multi_fact').sort((a, b) => a.sortOrder - b.sortOrder);
  const dimensionViews = views.filter((v) => v.viewType === 'dimension').sort((a, b) => a.sortOrder - b.sortOrder);

  if (views.length === 0) {
    return (
      <div className={styles.panel}>
        <div className={styles.empty}>
          <Text>No semantic views generated yet.</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      {factViews.length > 0 && (
        <ViewGroup title="Fact Views" count={factViews.length} views={factViews} selectedViewId={selectedViewId} onViewSelect={onViewSelect} />
      )}
      {multiFactViews.length > 0 && (
        <ViewGroup title="Multi-Fact Views" count={multiFactViews.length} views={multiFactViews} selectedViewId={selectedViewId} onViewSelect={onViewSelect} />
      )}
      {dimensionViews.length > 0 && (
        <ViewGroup title="Dimension Views" count={dimensionViews.length} views={dimensionViews} selectedViewId={selectedViewId} onViewSelect={onViewSelect} />
      )}
    </div>
  );
};

export default SemanticViewsPanel;